import { DateTime } from "luxon";
import { Roadmap, roadmapExample } from "./roadmapProps";
import {
  RoadmapItemDto,
  RoadmapItemSourceType,
} from "../../../../../customer-experience/src/lib/ShiOneClient";

export const roadmapHelpers = (
  text: string | undefined,
  maxLength: number,
): string => {
  if (text == undefined) {
    return "";
  }
  if (text.length > maxLength) {
    let truncated = text.slice(0, maxLength);
    truncated = truncated.slice(0, truncated.lastIndexOf(" "));
    truncated = truncated.replace(/[^a-zA-Z0-9]+$/, ""); // Remove trailing non-alphanumeric characters
    return truncated + "...";
  } else {
    return text;
  }
};

/**
 * Checks if a recommendation is added to the local roadmap state (not yet saved to database)
 */
export const isLocallyAdded = (
  sourceId: string,
  currentTasks?: RoadmapItemDto[],
): boolean => {
  if (!currentTasks?.length) return false;

  return currentTasks.some(
    (task) =>
      task.sourceId === sourceId &&
      !task.isDeleted &&
      task.sourceType === RoadmapItemSourceType.AssessmentRecommendation,
  );
};

/**
 * Checks if a recommendation was previously in the roadmap but has been locally removed
 */
export const isLocallyRemoved = (
  sourceId: string,
  locallyRemovedTasks?: RoadmapItemDto[],
): boolean => {
  if (!locallyRemovedTasks?.length) return false;

  return locallyRemovedTasks.some(
    (task) =>
      task.sourceId === sourceId &&
      task.sourceType === RoadmapItemSourceType.AssessmentRecommendation,
  );
};

export const formatScheduleDate = (date: Date | null): string => {
  if (date === null) {
    return "Invalid date";
  }

  const datetimeObject = DateTime.fromJSDate(date);
  return datetimeObject.toFormat("M/d/yyyy");
};

export const calculateDaysBetweenDates = (
  startDate: Date,
  endDate: Date,
): number => {
  const dt1 = DateTime.fromJSDate(startDate);
  const dt2 = DateTime.fromJSDate(endDate);
  return Math.abs(dt2.diff(dt1, "days").days) + 1;
};

export const getRoadmapById = (
  roadmapId: string | undefined,
): Roadmap | undefined => {
  return roadmapExample.find((roadmap) => roadmap.id === roadmapId);
};

export const formatDollarAmount = (cost: number | undefined): string => {
  const formattedCost = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cost || 0);

  return formattedCost.replace(/^(\$)/, "$ ");
};

export const formatDecimal = (
  number: number,
  decimalPlaces: number,
): number => {
  return Number(
    number.toLocaleString(undefined, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
      useGrouping: true, // Optional: Use thousands separators (e.g., commas)
    }),
  );
};

export const getRoadmapSchedule = (
  initiatives: RoadmapItemDto[],
): { startDate: Date; targetDate: Date } => {
  const initiativeTimes = initiatives.flatMap((item) => [
    item.startDate,
    item.targetDate,
  ]);

  const startDate = new Date(
    Math.min(
      ...initiativeTimes
        .filter((date): date is Date => date !== undefined)
        .map((date) => date.getTime()),
    ),
  );
  const targetDate = new Date(
    Math.max(
      ...initiativeTimes
        .filter((date): date is Date => date !== undefined)
        .map((date) => date.getTime()),
    ),
  );

  return { startDate, targetDate };
};
